/**
 * Product ALLOWANCES — what a bid carries for a product nobody has chosen yet.
 *
 * Most of the catalog holds products with no price, so a take-off row for a
 * tub, a range or a slab reached the end of the price ladder and billed $0:
 * every slab in the smell-test corpus and every kitchen appliance (measured
 * 2026-09-22, `claude-docs/2026-09-22-product-allowances.md`). An allowance is
 * also what a contractor really writes at bid time, because the homeowner has
 * not picked the fridge yet.
 *
 * Three tiers per entry, because a bid shows a RANGE (the user's decision,
 * 2026-09-22): `low` is builder grade, `typical` is what a mid-market remodel
 * buys, `high` is upper mainstream — not an ultra-luxury outlier.
 *
 * ## Where the numbers come from
 *
 * Two published sources, named per entry, never averaged together:
 *
 *   NCE     Craftsman's National Construction Estimator 2026, Material column
 *           — what a BUILDER pays. It prices by grade and cites a page. It
 *           sits at or below the market's low in every category where both
 *           cover the item.
 *   Guides  HomeGuide / HomeAdvisor / Fixr / Angi / Homewyse / Consumer
 *           Reports, plus real retailer SKUs — what a REMODEL buys.
 *
 * Where both cover an item they agree (toilets, cooktops, dishwashers, vanity
 * light bars, sconces), which is the strongest evidence available that the
 * band is right. Two places NCE is simply too low and the market wins: range
 * hoods (its $294 top against the $700–1,500 chimney hood a mid-market kitchen
 * buys) and tubs.
 *
 * ## Two rules that keep this from double-billing
 *
 * - **A row follows ONE source's bundling convention.** NCE's fixture prices
 *   include trim (a stated $320 on a tub, $185 on a kitchen sink); the guides'
 *   do not. We bill trim as its own rows, so every entry below that came from
 *   NCE is net of the stated allowance, and the rest are market prices that
 *   never included trim.
 * - **A set is not a piece.** Bath hardware sells four-to-a-set at about a
 *   third of the single-piece price, and we count pieces, so the hardware
 *   entries take NCE's per-piece builder figures rather than a specialty
 *   retailer's.
 *
 * `derived: true` marks a `typical` that is the midpoint of a source's
 * published band rather than a figure the source itself publishes. Nothing
 * here is invented: where no source publishes a figure, there is no entry.
 *
 * Pure data. No React, no Firestore, no environment.
 */

const nce = (page) => `NCE 2026 p.${page} (material column, read 2026-09-22)`;

/**
 * Keyed by the take-off row's `objectType`, or `objectType:variant` where one
 * type is really two products (a recessed can and a pendant are both
 * `ceiling-light`). Area-billed tops are keyed by `surfaceType`.
 */
const PRODUCT_ALLOWANCES = {
  // --- kitchen and laundry appliances -------------------------------------
  refrigerator: {
    unit: 'each',
    low: 430,
    typical: 1450,
    high: 3000,
    derived: true,
    source:
      'HomeAdvisor appliance pricing (updated 2026-06-17); HomeGuide (2026-01-02). NCE has no residential refrigerator line. Built-in/integrated is a tier above this: $5,000–12,000+ (Consumer Reports 2026).',
  },
  range: {
    unit: 'each',
    low: 410,
    typical: 950,
    high: 2500,
    derived: true,
    source:
      'HomeAdvisor (2026-06-17); HomeGuide stove/oven costs. Freestanding 30"; the high is mainstream, not pro-style.',
  },
  cooktop: {
    unit: 'each',
    low: 388,
    typical: 844,
    high: 2330,
    source: `${nce(255)} — gas 4-burner / gas 5-burner / induction. The market band ($320–2,500) agrees.`,
  },
  oven: {
    unit: 'each',
    low: 1250,
    typical: 1470,
    high: 2380,
    source: `${nce(255)} — 30" built-in self-cleaning, black / stainless / thermal. A double oven is a different product ($2,710–5,440).`,
  },
  dishwasher: {
    unit: 'each',
    low: 469,
    typical: 879,
    high: 1890,
    source: `${nce(248)} — NCE's own economy / standard / deluxe grades. The market band ($330–2,100) agrees.`,
  },
  microwave: {
    unit: 'each',
    low: 150,
    typical: 375,
    high: 1800,
    derived: true,
    source:
      'HomeGuide microwave costs (2026); Angi. Over-the-range; NCE has no residential line.',
  },
  'range-hood': {
    unit: 'each',
    low: 100,
    typical: 350,
    high: 1500,
    derived: true,
    source:
      'Fixr range hood, material-only table (2025-01-31); HomeGuide (2026). Deliberately NOT NCE, whose $111–294 prices a basic under-cabinet hood and misses the stainless chimney hood a mid-market kitchen buys.',
  },
  washer: {
    unit: 'each',
    low: 445,
    typical: 1000,
    high: 2300,
    derived: true,
    source: 'HomeAdvisor (2026-06-17); HomeGuide washer and dryer costs.',
  },
  dryer: {
    unit: 'each',
    low: 445,
    typical: 1000,
    high: 2100,
    derived: true,
    source:
      'HomeAdvisor (2026-06-17); HomeGuide (gas runs $90–100 over electric).',
  },
  'washer-dryer': {
    unit: 'each',
    low: 1000,
    typical: 1450,
    high: 2500,
    derived: true,
    source: 'Fixr washer-dryer combo (2025-01-31); HomeGuide (2026).',
  },

  // --- plumbing fixtures ---------------------------------------------------
  toilet: {
    unit: 'each',
    low: 262,
    typical: 484,
    high: 655,
    source: `${nce(247)} — floor-mount tank / one-piece / pressure assist, including seat and supply. The market agrees ($100–500 two-piece, $200–900 one-piece). A smart or bidet toilet is a tier above ($500–2,000, HomeGuide).`,
  },
  sink: {
    unit: 'each',
    low: 40,
    typical: 270,
    high: 500,
    derived: true,
    source:
      'HomeGuide sink costs, unit-price column (2026) — undermount lavatory. NOT from NCE, whose lavatory lines bundle a faucet of unstated value.',
  },
  'kitchen-sink': {
    unit: 'each',
    low: 150,
    typical: 575,
    high: 1000,
    derived: true,
    source:
      'HomeGuide sink costs (2026), stainless undermount. A farmhouse sink is a tier above ($200–3,500).',
  },
  faucet: {
    unit: 'each',
    low: 30,
    typical: 160,
    high: 900,
    derived: true,
    source:
      'HomeGuide faucet costs (2026) — bathroom sink faucet, published band $70–250.',
  },
  'kitchen-faucet': {
    unit: 'each',
    low: 40,
    typical: 240,
    high: 700,
    derived: true,
    source:
      'HomeGuide faucet costs (2026), published band $130–350; Angi $70–900.',
  },
  'tub-faucet': {
    unit: 'each',
    low: 80,
    typical: 250,
    high: 1000,
    derived: true,
    source:
      'HomeGuide faucet costs (2026), published band $150–350. NOTE: a tub faucet KIT often includes the shower trim, which we bill separately.',
  },
  bathtub: {
    unit: 'each',
    low: 200,
    typical: 650,
    high: 1200,
    derived: true,
    source:
      'Fixr (2025-08-29, materials alone); HomeGuide tub-price-only column; Angi (2026-07-09). Alcove acrylic. Cast iron $600–3,000 and freestanding $2,000–3,000 are separate products.',
  },
  shower: {
    unit: 'each',
    low: 400,
    typical: 1200,
    high: 2000,
    derived: true,
    source:
      'HomeGuide shower insert costs (2026), prefab fiberglass. A kit may or may not include its door and pan.',
  },
  // the pan of a prefab shower kit, alone: what a design shower carries
  // where a glass bundle supplies its glass (user ruling, 2026-10-25), so
  // the glass is not in this row and that one (`estimate/showerGlassSupply.js`)
  'shower-pan': {
    unit: 'each',
    low: 150,
    typical: 410,
    high: 950,
    derived: true,
    source:
      'HomeGuide shower pan / base (gathered 2026-09-22, claude-docs/2026-09-22-product-allowances.md), the pan alone.',
  },
  showerhead: {
    unit: 'each',
    low: 5,
    typical: 88,
    high: 300,
    derived: true,
    source: 'Angi shower head (2026-08-03), rainfall band $25–150.',
  },
  'shower-control': {
    unit: 'each',
    low: 50,
    typical: 225,
    high: 300,
    derived: true,
    source:
      'HomeGuide shower faucet, faucet-price-only (2026), pressure-balancing band $150–300. Trim only; the in-wall valve body is rough-in.',
  },

  // --- lighting ------------------------------------------------------------
  'ceiling-light': {
    unit: 'each',
    low: 30,
    typical: 65,
    high: 100,
    derived: true,
    source:
      "HomeGuide lighting (2025-08-29), fixture alone. NCE's LED flush mounts ($29.80–54.60) agree. Homewyse's $95–228 prices delivered contractor-grade material, a different question.",
  },
  'ceiling-light:recessed': {
    unit: 'each',
    low: 15,
    typical: 75,
    high: 161,
    derived: true,
    source:
      'Home Depot multipacks per unit (read 2026-09-22); HomeGuide $50–100 for the fixture alone; Homewyse (2026-09). No published 4" vs 6" split exists.',
  },
  'ceiling-light:pendant': {
    unit: 'each',
    low: 50,
    typical: 160,
    high: 250,
    derived: true,
    source:
      'HomeGuide lighting (2025-08-29), $50–250 for the fixture alone; Homewyse $95–226. Luxury pendants exceed $1,000.',
  },
  'wall-light': {
    unit: 'each',
    low: 30,
    typical: 65,
    high: 100,
    derived: true,
    source:
      "HomeGuide lighting (2025-08-29), $30–100 for the fixture itself; NCE's LED sconces ($28.80–44.60) agree. No source publishes a median, and $100 is the guide's ceiling rather than the market's.",
  },
  'overhead-wall-light': {
    unit: 'each',
    low: 25,
    typical: 60,
    high: 103,
    derived: true,
    source:
      'Home Depot 3-light bath bars (read 2026-09-22); NCE p.190 LED vanity lighting ($49.60–99.20) agrees closely — the best agreement in the study.',
  },

  // --- bath hardware and mirrors ------------------------------------------
  mirror: {
    unit: 'each',
    low: 20,
    typical: 150,
    high: 600,
    derived: true,
    source:
      'HomeGuide mirror costs (2025-08-29) — framed $50–250, glass-only column to $600. NCE p.23 framed mirrors ($114–292) sit inside it.',
  },
  'towel-bar': {
    unit: 'each',
    low: 17.9,
    typical: 25.2,
    high: 61.3,
    source: `${nce(24)} — chrome 18" / chrome 30" / solid brass 24", per piece. A 4-piece set is ~$15/piece, so single-piece retail (to $130) would over-allow a builder-grade job ~3x.`,
  },
  'towel-ring': {
    unit: 'each',
    low: 29,
    typical: 55,
    high: 87,
    source:
      'NCE p.24 ($52.80–55.20) for the middle; Signature Hardware listings (read 2026-09-22) for the ends. No cost guide publishes a towel-ring figure.',
  },
  'toilet-paper-holder': {
    unit: 'each',
    low: 21.9,
    typical: 54,
    high: 129,
    source: `${nce(24)} chrome recessed for the low; Signature Hardware (read 2026-09-22) above it. No cost guide publishes one.`,
  },
  'robe-hook': {
    unit: 'each',
    low: 23,
    typical: 48,
    high: 87,
    source: `${nce(24)} ($28.20–29.20) and Signature Hardware (read 2026-09-22). No cost guide publishes one.`,
  },

  // --- built on site, so the allowance is only what it is BUILT FROM ------
  // Their labor is already billed (`install-shower-niche`, `frame-tub-*`),
  // the slab on a platform is its own `tub-surface` row, and the tile around
  // them is in the room's tile area. What was missing is the material.
  'shower-niche': {
    unit: 'each',
    low: 75,
    typical: 188,
    high: 300,
    derived: true,
    source:
      'Angi (2026): a prefabricated shower niche is $75–300. A custom tiled niche is $300–700 there, but that figure carries its own tile and labor, which we bill as their own lines.',
  },
  'tub-support-box:platform': {
    unit: 'each',
    low: 45,
    typical: 150,
    high: 255,
    derived: true,
    source:
      'Fixr deck framing, material only, $3–17/ft² (2026), over the ~15 ft² of a 60" x 36" drop-in platform — the size NCE itself uses for a tub support (p.245). Where a tub needs an ENGINEERED support, NCE prices that system at $636–768 (p.245), well above a site-built stud deck.',
  },
  'tub-support-box:bookend': {
    unit: 'each',
    low: 15,
    typical: 40,
    high: 70,
    derived: true,
    source:
      'Fixr deck framing, material only, $3–17/ft² (2026), over the ~4 ft² of the box that closes the gap beside an alcove tub.',
  },

  // --- area finishes, per ft² ---------------------------------------------
  tile: {
    unit: 'sqft',
    low: 2.56,
    typical: 5.64,
    high: 9.9,
    source: `${nce(427)} — glazed wall tile, minimum / standard / high-gloss quality. Most catalog textures carry a scraped retail price and never reach this rung; the ones that do are the unpriced mosaics and large-format stone, which run HIGHER: ceramic mosaic $5.77, glass $16.10, natural stone $23.80, marble tile $8.17-11.40 (p.287).`,
  },

  // The floor a re-laid-out room gets before anybody has chosen one (user,
  // 2026-10-20): luxury vinyl plank, the commonest remodel floor. NCE's
  // figures include 15% cutting waste and the take-off adds its own (vinyl
  // 7%), so these are NCE's divided by 1.15 — net, like every other entry.
  'unchosen-floor': {
    unit: 'sqft',
    low: 2.92,
    typical: 3.88,
    high: 4.28,
    source: `NCE 2026 p.154 (material column, read 2026-10-20) — luxury vinyl plank: economy glue-down / most colours, floating / premium waterproof, net of the 15% cutting waste NCE includes. Only the allowance row of a re-laid-out room's unchosen floor is keyed here (\`allowanceKey\`), never a vinyl product with no price.`,
  },

  // --- cabinet accessories -----------------------------------------------
  // A roll-out tray behind a base's doors, priced alone (its install is
  // `install-rollout-shelf`). No colour (cabinet-interior stock). NCE 2026
  // lists no roll-out tray, so the tiers are three live retailer SKUs for a
  // 24" base, each one tray.
  'cabinet-rollout': {
    unit: 'each',
    low: 31,
    typical: 73.99,
    high: 136.9,
    source:
      'Home Depot, read 2026-10-25: Hampton Bay ADRTA21 21 in. pull-out drawer $31 / Rev-A-Shelf 5WB1-2120CR-1 21 x 20 in. wire pull-out $73.99 / Rev-A-Shelf 4WDB-21SC-1 20.71 in. wood pull-out drawer with soft-close $136.90. Cross-check: Fixr, via Bob Vila, $140–350 a shelf installed (avg $245).',
  },

  // --- slab tops, per ft² of plan area ------------------------------------
  countertop: {
    unit: 'sqft',
    low: 61.7,
    typical: 73,
    high: 99.3,
    source: `${nce(96)} granite 2cm / engineered quartz / marble (p.208). The market's material-only bands are wider ($40–170 quartz, $40–100 granite) and disagree with each other by 2-3x, so they are a cross-check only.`,
  },
  'tub-surface': {
    unit: 'sqft',
    low: 61.7,
    typical: 73,
    high: 99.3,
    source: `${nce(96)} — the deck slab on a tub platform is the same stone as a countertop, set by the same stone setter.`,
  },
  'corner-fill-countertop': {
    unit: 'sqft',
    low: 61.7,
    typical: 73,
    high: 99.3,
    source: `${nce(96)} — same stone, billed on the kite's polygon area.`,
  },
};

/**
 * Types that deliberately have NO allowance, so a reader does not take the
 * absence for an oversight:
 *
 *   blank-panel                     a filler strip, already seeded with the
 *                                   cabinetry it belongs to.
 *   cabinets                        already seeded per box; a grade
 *                                   multiplier scales them instead (see
 *                                   `CABINET_GRADE_MULTIPLIER`).
 *   paint, flooring                 priced per ft² from the catalog, which
 *                                   does carry prices for them.
 */
const NO_ALLOWANCE = new Set(['blank-panel']);

/**
 * Aliases onto the entries above — the take-off's type vocabulary has several
 * spellings for one product, and they must all price the same.
 */
const ALLOWANCE_ALIASES = {
  fridge: 'refrigerator',
  stove: 'range',
  'vent-hood': 'range-hood',
  washerDryer: 'washer-dryer',
  'bathroom-sink': 'sink',
  'bathroom-sink-faucet': 'faucet',
  'bathroom-tub-faucet': 'tub-faucet',
  tub: 'bathtub',
  'shower-controls': 'shower-control',
  sconce: 'wall-light',
  'pendant-light': 'ceiling-light:pendant',
  'recessed-light': 'ceiling-light:recessed',
};

module.exports = {
  PRODUCT_ALLOWANCES,
  NO_ALLOWANCE,
  ALLOWANCE_ALIASES,
};
